import React, { useState } from 'react';

export interface ReportConfigData {
  recipients: string[];
  topN: number;
  includeAllDef: boolean;
}

interface ReportConfigProps {
  config: ReportConfigData;
  onChange: (config: ReportConfigData) => void;
}

const TOP_N_OPTIONS = [
  { value: 20, label: '20 channels' },
  { value: 40, label: '40 channels (recommended)' },
  { value: 80, label: '80 channels' },
  { value: 162, label: 'All channels (~162)' },
];

const ReportConfig: React.FC<ReportConfigProps> = ({ config, onChange }) => {
  const [recipientInput, setRecipientInput] = useState('');
  const [recipientError, setRecipientError] = useState<string | null>(null);

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem',
      padding: '1rem',
      backgroundColor: 'var(--cg-neutral-25)',
      borderRadius: '8px',
      border: '1px solid var(--border-1)',
    },
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'var(--fg-1)',
      marginBottom: '0.25rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: 'var(--fg-1)',
    },
    select: {
      padding: '0.625rem 0.75rem',
      borderRadius: '6px',
      border: '1px solid var(--border-1)',
      fontSize: '0.875rem',
      backgroundColor: 'var(--bg-surface-1)',
      color: 'var(--fg-1)',
    },
    input: {
      padding: '0.625rem 0.75rem',
      borderRadius: '6px',
      border: '1px solid var(--border-1)',
      fontSize: '0.875rem',
      backgroundColor: 'var(--bg-surface-1)',
      color: 'var(--fg-1)',
    },
    recipientsList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
    },
    recipientTag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      backgroundColor: 'var(--accent)',
      color: 'white',
      borderRadius: '4px',
      fontSize: '0.75rem',
    },
    removeButton: {
      background: 'none',
      border: 'none',
      color: 'white',
      cursor: 'pointer',
      padding: '0',
      fontSize: '1rem',
      lineHeight: 1,
    },
    addRecipientRow: {
      display: 'flex',
      gap: '0.5rem',
    },
    addButton: {
      padding: '0.625rem 1rem',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: 'var(--accent)',
      color: 'white',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    errorText: {
      color: 'var(--danger)',
      fontSize: '0.75rem',
      marginTop: '0.25rem',
    },
    helpText: {
      color: 'var(--fg-2)',
      fontSize: '0.75rem',
      marginTop: '0.25rem',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    checkbox: {
      width: '16px',
      height: '16px',
    },
  };

  const validateRecipient = (email: string): string | null => {
    const emailLower = email.toLowerCase().trim();
    if (!emailLower.includes('@')) {
      return 'Invalid email format';
    }
    const domain = emailLower.split('@')[1];
    if (domain !== 'culturegenesis.com' && domain !== 'culturegenesis-partnerships.com') {
      return 'Recipients must be @culturegenesis.com or @culturegenesis-partnerships.com';
    }
    if (config.recipients.includes(emailLower)) {
      return 'Recipient already added';
    }
    return null;
  };

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (!email) return;

    const error = validateRecipient(email);
    if (error) {
      setRecipientError(error);
      return;
    }

    setRecipientError(null);
    onChange({
      ...config,
      recipients: [...config.recipients, email.toLowerCase()],
    });
    setRecipientInput('');
  };

  const removeRecipient = (email: string) => {
    onChange({
      ...config,
      recipients: config.recipients.filter((r) => r !== email),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addRecipient();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.sectionTitle}>Report Settings</div>

      {/* Recipients */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Send Report To</label>
        <div style={styles.addRecipientRow}>
          <input
            style={{ ...styles.input, flex: 1 }}
            type="email"
            placeholder="team@culturegenesis.com"
            value={recipientInput}
            onChange={(e) => {
              setRecipientInput(e.target.value);
              setRecipientError(null);
            }}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            style={styles.addButton}
            onClick={addRecipient}
          >
            Add
          </button>
        </div>
        {recipientError && <p style={styles.errorText}>{recipientError}</p>}
        {config.recipients.length === 0 && (
          <p style={styles.helpText}>
            Add at least one @culturegenesis.com recipient. If none are added, the report will be sent to the default team list.
          </p>
        )}
        <div style={styles.recipientsList}>
          {config.recipients.map((email) => (
            <span key={email} style={styles.recipientTag}>
              {email}
              <button
                type="button"
                style={styles.removeButton}
                onClick={() => removeRecipient(email)}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Top N */}
      <div style={styles.formGroup}>
        <label style={styles.label}>Number of channels to include</label>
        <select
          style={styles.select}
          value={config.topN}
          onChange={(e) => onChange({ ...config, topN: Number(e.target.value) })}
        >
          {TOP_N_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Include All Def */}
      <div style={styles.formGroup}>
        <div style={styles.checkboxRow}>
          <input
            type="checkbox"
            id="includeAllDef"
            style={styles.checkbox}
            checked={config.includeAllDef}
            onChange={(e) =>
              onChange({ ...config, includeAllDef: e.target.checked })
            }
          />
          <label htmlFor="includeAllDef" style={styles.label}>
            Include All Def CMS channels
          </label>
        </div>
        <p style={styles.helpText}>
          When enabled, channels from both Culture Genesis and All Def CMSes are included.
        </p>
      </div>
    </div>
  );
};

export default ReportConfig;
